import { FC, useMemo, useEffect, useRef } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { Replay } from "@mui/icons-material";
import { FilterChip } from "../../../../../../components/filters/FilterChip";
import {
  InstallFilters as InstallFiltersType,
  useCompanyPageStore,
} from "../../../../../../store/companyPageStore";
import { useInstallFiltersStorage } from "./hooks/useInstallFiltersStorage";
import { GeographyFilter } from "./GeographyFilter";
import { ProductAttributesFilter } from "./ProductAttributesFilter";
import { ProductsFilter } from "./ProductsFilter";
import { VendorsFilter } from "./VendorsFilter";
import { ProductCategoriesFilter } from "./ProductCategoriesFilter";
import { useFilterDraft } from "@/hooks/filters/useFilterDraft";
import { useSimpleProductsLabel } from "../../../../../../hooks/filters/useSimpleProductsLabel";
import { useSimpleVendorsLabel } from "../../../../../../hooks/filters/useSimpleVendorsLabel";
import { useGeographyLabel } from "../../../../../../hooks/useGeographyLabel";
import { useProductAttributesLabel } from "../../../../../Workspace/UserFilters/FiltersChips/hooks/useProductAttributesLabel";
import { useProductCategoriesLabel } from "../../../../../Workspace/UserFilters/FiltersChips/hooks/useProductCategoriesLabel";

export const InstallFilters: FC = () => {
  const company = useCompanyPageStore((state) => state.company);
  const { installFilters, setInstallFilters, resetInstallFilters } =
    useInstallFiltersStorage();
  const hasInitialized = useRef(false);

  // Draft states for each filter
  const geographyDraft = useFilterDraft(
    installFilters.countryAlpha2s,
    (value) => setInstallFilters({ ...installFilters, countryAlpha2s: value })
  );

  const productAttributesDraft = useFilterDraft(
    installFilters.productAttributeIds,
    (value) =>
      setInstallFilters({ ...installFilters, productAttributeIds: value })
  );

  const productsDraft = useFilterDraft(installFilters.productIds, (value) =>
    setInstallFilters({ ...installFilters, productIds: value })
  );

  const vendorsDraft = useFilterDraft(installFilters.vendorIds, (value) =>
    setInstallFilters({ ...installFilters, vendorIds: value })
  );

  const productCategoriesDraft = useFilterDraft(
    installFilters.productCategoryIds,
    (value) =>
      setInstallFilters({ ...installFilters, productCategoryIds: value })
  );

  // Labels based on applied filters (not draft)
  const geographyLabel = useGeographyLabel({
    countryAlpha2s: installFilters.countryAlpha2s,
  });

  const productAttributesLabel = useProductAttributesLabel(
    installFilters.productAttributeIds
  );

  const productsLabel = useSimpleProductsLabel(installFilters.productIds);

  const vendorsLabel = useSimpleVendorsLabel(installFilters.vendorIds);

  const productCategoriesLabel = useProductCategoriesLabel(
    installFilters.productCategoryIds
  );

  const handleClearFilter = (filterKey: keyof InstallFiltersType) => {
    if (filterKey === "includeCorporateDescendants") {
      setInstallFilters({
        ...installFilters,
        [filterKey]: false,
      });
    } else {
      setInstallFilters({
        ...installFilters,
        [filterKey]: [],
      });
    }
  };

  const handleToggleCorporateDescendants = () => {
    setInstallFilters({
      ...installFilters,
      includeCorporateDescendants: !installFilters.includeCorporateDescendants,
    });
  };

  const hasFiltersApplied = useMemo(() => {
    return (
      installFilters.countryAlpha2s.length > 0 ||
      installFilters.productAttributeIds.length > 0 ||
      installFilters.productIds.length > 0 ||
      installFilters.vendorIds.length > 0 ||
      installFilters.productCategoryIds.length > 0 ||
      installFilters.includeCorporateDescendants
    );
  }, [installFilters]);

  // Check if corporate toggle should be shown
  const shouldShowCorporateToggle = useMemo(() => {
    return (
      company &&
      company.hgId === company.orgTree?.root &&
      (company.orgTree?.descendants.length ?? 0) > 1
    );
  }, [company]);

  // Set default country filter ONLY when filters are fresh (no stored filters for this company)
  useEffect(() => {
    const companyCountry = company?.locationPrimary?.state?.country?.alpha2;

    if (
      !hasInitialized.current &&
      company?.hgId &&
      companyCountry &&
      installFilters.countryAlpha2s.length === 0
    ) {
      setInstallFilters({
        ...installFilters,
        countryAlpha2s: [companyCountry],
        companyId: company.hgId,
      });
      hasInitialized.current = true;
    }

    // Mark as initialized even if we don't set defaults
    if (!hasInitialized.current) {
      hasInitialized.current = true;
    }
  }, [company, installFilters, setInstallFilters]);

  return (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      alignItems="center"
      justifyContent="space-between"
    >
      {/* Filter Chips */}
      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
        {/* Geography Filter */}
        <FilterChip
          label="Geography"
          getSelectedCount={() => installFilters.countryAlpha2s.length}
          onDelete={
            installFilters.countryAlpha2s.length > 0
              ? () => handleClearFilter("countryAlpha2s")
              : undefined
          }
          renderLabel={() => (
            <Stack flexDirection="row" gap="3px">
              <Typography color="secondary">Geography:</Typography>
              <Typography>{geographyLabel || "All"}</Typography>
            </Stack>
          )}
          popoverOptions={{
            onClearButtonClick: geographyDraft.clearDraft,
            onCancelButtonClick: geographyDraft.cancelChanges,
            onApplyButtonClick: geographyDraft.applyChanges,
            disableApplyButton: !geographyDraft.hasChanges,
            disableCancelButton: false,
          }}
        >
          <GeographyFilter
            value={geographyDraft.draft}
            onChange={geographyDraft.updateDraft}
          />
        </FilterChip>

        {/* Product Attributes Filter */}
        <FilterChip
          label="Product Attributes"
          getSelectedCount={() => installFilters.productAttributeIds.length}
          onDelete={
            installFilters.productAttributeIds.length > 0
              ? () => handleClearFilter("productAttributeIds")
              : undefined
          }
          renderLabel={() => (
            <Stack flexDirection="row" gap="3px">
              <Typography color="secondary">Product Attributes:</Typography>
              <Typography>{productAttributesLabel}</Typography>
            </Stack>
          )}
          popoverOptions={{
            onClearButtonClick: productAttributesDraft.clearDraft,
            onCancelButtonClick: productAttributesDraft.cancelChanges,
            onApplyButtonClick: productAttributesDraft.applyChanges,
            disableApplyButton: !productAttributesDraft.hasChanges,
            disableCancelButton: false,
          }}
        >
          <ProductAttributesFilter
            value={productAttributesDraft.draft}
            onChange={productAttributesDraft.updateDraft}
          />
        </FilterChip>

        {/* Products Filter */}
        <FilterChip
          label="Products"
          getSelectedCount={() => installFilters.productIds.length}
          onDelete={
            installFilters.productIds.length > 0
              ? () => handleClearFilter("productIds")
              : undefined
          }
          renderLabel={() => (
            <Stack flexDirection="row" gap="3px">
              <Typography color="secondary">Products:</Typography>
              <Typography>{productsLabel}</Typography>
            </Stack>
          )}
          popoverOptions={{
            onClearButtonClick: productsDraft.clearDraft,
            onCancelButtonClick: productsDraft.cancelChanges,
            onApplyButtonClick: productsDraft.applyChanges,
            disableApplyButton: !productsDraft.hasChanges,
            disableCancelButton: false,
          }}
        >
          <ProductsFilter
            value={productsDraft.draft}
            onChange={productsDraft.updateDraft}
          />
        </FilterChip>

        {/* Vendors Filter */}
        <FilterChip
          label="Vendors"
          getSelectedCount={() => installFilters.vendorIds.length}
          onDelete={
            installFilters.vendorIds.length > 0
              ? () => handleClearFilter("vendorIds")
              : undefined
          }
          renderLabel={() => (
            <Stack flexDirection="row" gap="3px">
              <Typography color="secondary">Vendors:</Typography>
              <Typography>{vendorsLabel}</Typography>
            </Stack>
          )}
          popoverOptions={{
            onClearButtonClick: vendorsDraft.clearDraft,
            onCancelButtonClick: vendorsDraft.cancelChanges,
            onApplyButtonClick: vendorsDraft.applyChanges,
            disableApplyButton: !vendorsDraft.hasChanges,
            disableCancelButton: false,
          }}
        >
          <VendorsFilter
            value={vendorsDraft.draft}
            onChange={vendorsDraft.updateDraft}
          />
        </FilterChip>

        {/* Product Categories Filter */}
        <FilterChip
          label="Product Categories"
          getSelectedCount={() => installFilters.productCategoryIds.length}
          onDelete={
            installFilters.productCategoryIds.length > 0
              ? () => handleClearFilter("productCategoryIds")
              : undefined
          }
          renderLabel={() => (
            <Stack flexDirection="row" gap="3px">
              <Typography color="secondary">Product Categories:</Typography>
              <Typography>{productCategoriesLabel}</Typography>
            </Stack>
          )}
          popoverOptions={{
            onClearButtonClick: productCategoriesDraft.clearDraft,
            onCancelButtonClick: productCategoriesDraft.cancelChanges,
            onApplyButtonClick: productCategoriesDraft.applyChanges,
            disableApplyButton: !productCategoriesDraft.hasChanges,
            disableCancelButton: false,
          }}
        >
          <ProductCategoriesFilter
            value={productCategoriesDraft.draft}
            onChange={productCategoriesDraft.updateDraft}
          />
        </FilterChip>

        {/* Reset Filters Button */}
        {hasFiltersApplied && (
          <Tooltip title="Reset Filters">
            <IconButton
              size="small"
              sx={{ height: 24, width: 24 }}
              onClick={resetInstallFilters}
            >
              <Replay sx={{ height: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* Corporate Descendants Toggle - Right aligned */}
      {shouldShowCorporateToggle && (
        <FormControlLabel
          control={
            <Switch
              checked={installFilters.includeCorporateDescendants || false}
              onChange={handleToggleCorporateDescendants}
              size="small"
            />
          }
          label={
            <Typography variant="body2">
              Show installs across the entire organization
            </Typography>
          }
        />
      )}
    </Stack>
  );
};
